import { useEffect, useRef } from 'react';
import {
  deriveStrings,
  useFlashStore,
  useGestureStore,
  useHandStore,
  useSettingsStore,
  useTuningStore,
  useUiStore,
} from '@store/index';
import { computeStringYs } from '@engine/scale';
import { FINGER_TIPS } from '@engine/hand/landmarks';
import { UI } from '@config/constants';

/** How long the string glow stays visible after a pluck. Matched to the
 *  audible portion of `PluckVoice` (DECAY_TAIL_SEC = 3.5s, but the last
 *  ~1s is effectively inaudible) so the visual decay tracks the sound
 *  instead of popping off after a glance. */
const FLASH_TTL_MS = 2500;

/** Horizontal σ of the bend bump — controls how many strings laterally
 *  get affected around the fingertip. */
const BEND_SIGMA_X = 0.06;
/** Vertical σ — controls how many string rows (above/below the fingertip)
 *  bend along. Tight so only strings near the press point move. */
const BEND_SIGMA_Y = 0.05;

interface BendVisual {
  /** X of the fingertip centroid (mirror-display space). */
  apexX: number;
  /** Y of the fingertip centroid (where the fingers ARE right now). */
  apexY: number;
  /** Y of the bend anchor (where the fingers WERE when bend started). */
  anchorY: number;
}

/**
 * Horizontal strings (edge-to-edge) + velocity-scaled glow on pluck +
 * curved-string visualization when a hand is bending.
 *
 * Bend visual: each string is drawn as a quadratic Bezier curve with
 * its apex at the bending hand's X position and a vertical offset
 * proportional to the bend amount. Positive bend (pitch up) pulls the
 * string UP (apex y smaller). Negative bend (pitch down) pulls it DOWN.
 *
 * If both hands are bending, both curves are applied additively via
 * a sampled polyline per string.
 */
export function StringsOverlay() {
  const ref = useRef<HTMLCanvasElement>(null);
  const activeIndex = useTuningStore((s) => s.activeIndex);
  const activeSet = useTuningStore((s) => s.sets[s.activeIndex]!);
  const stringCount = activeSet.stringCount;
  const mirrorCamera = useSettingsStore((s) => s.mirrorCamera);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * devicePixelRatio;
      canvas.height = rect.height * devicePixelRatio;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      const t = useTuningStore.getState();
      const active = t.sets[t.activeIndex]!;
      const tuning = deriveStrings(active);
      const ui = useUiStore.getState();
      const stringYs = computeStringYs(active.stringCount, {
        spacing: ui.stringSpacing,
        orderLowTop: ui.stringOrderLowTop,
        pitches: tuning.map((d) => d.midi + d.cents / 100),
      });
      const flashes = useFlashStore.getState().flashes;
      const gesture = useGestureStore.getState();
      const hands = useHandStore.getState();
      const now = performance.now();

      // Collect active bends — apex tracks the live fingertip centroid and
      // the vertical displacement is the RAW finger travel from the bend
      // anchor. That way the string curve literally follows the hand.
      const bends: BendVisual[] = [];
      for (const id of ['left', 'right'] as const) {
        const g = gesture[id];
        if (g.mode !== 'bend') continue;
        const hand = hands[id];
        if (!hand.present || !hand.landmarks) continue;
        const anchorY = g.bendAnchorX; // misnomer — actually Y
        if (anchorY === null) continue;

        const idx = hand.landmarks[FINGER_TIPS.index];
        const mid = hand.landmarks[FINGER_TIPS.middle];
        const ring = hand.landmarks[FINGER_TIPS.ring];
        if (!idx || !mid || !ring) continue;

        const avgX = (idx.x + mid.x + ring.x) / 3;
        const avgY = (idx.y + mid.y + ring.y) / 3;
        const apexX = mirrorCamera ? 1 - avgX : avgX;

        if (avgY === anchorY) continue;
        bends.push({ apexX, apexY: avgY, anchorY });
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineCap = 'round';

      const dpr = devicePixelRatio;
      const fontSize = 11 * dpr;
      ctx.font = `500 ${fontSize}px -apple-system, system-ui, sans-serif`;
      ctx.textBaseline = 'middle';

      const padX = 10 * dpr;
      const pillPadH = 8 * dpr;
      const pillPadV = 3 * dpr;

      // Absolute thickness mapping: a string at C1 always looks thick,
      // a string at C8 always looks thin, regardless of what else is
      // in the current set. Using MIDI as the independent variable
      // keeps the scale log-in-frequency (matching physics: pitch
      // doubles every octave), and a power curve emphasises the bass.
      //   MIDI_LOW  (24, C1) → MAX_W
      //   MIDI_HIGH (108, C8) → MIN_W
      const MIDI_LOW = 24;
      const MIDI_HIGH = 108;
      const MIN_W = 0.6;
      const MAX_W = 6.0;

      for (let i = 0; i < stringCount; i++) {
        const yBase = (stringYs[i] ?? 0.5) * canvas.height;
        const tun = tuning[i]!;
        const isRoot = tun.isRoot;
        const isP5 = tun.isPerfectFifth;
        // Root and fifth are both "anchor" strings but keep distinct
        // colours so the player can tell them apart at a glance.
        const anchorColor = isRoot
          ? UI.COLORS.ACCENT // yellow — the root
          : isP5
            ? UI.COLORS.FIFTH // red — the perfect fifth
            : null;
        const isHome = anchorColor !== null;

        const path = buildStringPath(yBase, bends, canvas.width, canvas.height);

        const norm = Math.max(
          0,
          Math.min(1, (MIDI_HIGH - tun.midi) / (MIDI_HIGH - MIDI_LOW)),
        );
        // Power curve (1.6) makes the bass end taper up sharply — so
        // register 1 vs register 3 look obviously different, not just
        // nudged. Squared-ish keeps the high end visibly thin.
        const physWidth = MIN_W + (MAX_W - MIN_W) * Math.pow(norm, 1.6);
        const physWidthPx = physWidth * dpr;
        ctx.strokeStyle = anchorColor ?? 'rgba(255,255,255,0.92)';
        ctx.lineWidth = isHome ? physWidthPx + 0.8 * dpr : physWidthPx;
        ctx.stroke(path);

        // Vertical gap to the closer of the two neighbouring strings (in
        // device pixels). Bounds the loudest glow so it can REACH toward
        // the neighbour without crossing it. For the first/last string we
        // use the same-side gap twice.
        const yNeighbourUp =
          i > 0 ? (stringYs[i - 1] ?? stringYs[i] ?? 0.5) * canvas.height : null;
        const yNeighbourDn =
          i < stringCount - 1
            ? (stringYs[i + 1] ?? stringYs[i] ?? 0.5) * canvas.height
            : null;
        const gaps: number[] = [];
        if (yNeighbourUp !== null) gaps.push(Math.abs(yBase - yNeighbourUp));
        if (yNeighbourDn !== null) gaps.push(Math.abs(yBase - yNeighbourDn));
        const gapPx = gaps.length ? Math.min(...gaps) : physWidthPx * 6;

        // --- Flash glow (reuses same Path2D) ---
        // Glow tint matches the hand that plucked: left-hand plucks
        // glow sky-blue (matching the tracking skeleton), right-hand
        // glow salmon. Falls back to gold when the hand is unknown.
        //
        // Width: at the SOFTEST pluck the outer glow is 2× the string's
        // own width (just barely perceptible); at the LOUDEST the outer
        // glow diameter reaches ~92% of the distance to the closest
        // neighbour, so two adjacent glows almost touch. Interpolated
        // linearly on velocity, then stretched through a power curve for
        // a juicier soft→loud arc.
        const flash = flashes[i];
        if (flash) {
          const age = now - flash.at;
          if (age < FLASH_TTL_MS) {
            const t = age / FLASH_TTL_MS;
            const fade = (1 - t) * (1 - t);
            const v = flash.velocity;
            const intensity = v * fade;
            // Outer glow width (total stroke width in pixels).
            const softOuterPx = 2 * physWidthPx;
            const loudOuterPx = Math.max(softOuterPx, gapPx * 1.85);
            const velExp = Math.pow(v, 0.7); // expand the soft→loud arc
            const outerPx = softOuterPx + (loudOuterPx - softOuterPx) * velExp;
            // flash.hand is the PHYSICAL hand, computed at pluck time
            // from wrist x-position in the gesture loop. No mirror swap
            // needed — MediaPipe label flicker can't color-flip a flash
            // because the write-side already resolved to the real hand.
            const glowRgb =
              flash.hand === 'left'
                ? '125, 211, 252' // UI.COLORS.LEFT_HAND (sky-300)
                : flash.hand === 'right'
                  ? '252, 165, 165' // UI.COLORS.RIGHT_HAND (salmon)
                  : '255, 210, 74'; // gold fallback (preview / no hand)
            // Three concentric halo layers — outer is the wide, dim
            // corona; middle is the mid-density body; inner is the
            // bright body-centre. Alpha scales with age-fade × velocity.
            const layers: Array<[number, number]> = [
              [outerPx, 0.28],
              [outerPx * 0.55, 0.55],
              [outerPx * 0.28, 0.9],
            ];
            for (const [w, a] of layers) {
              ctx.strokeStyle = `rgba(${glowRgb}, ${a * intensity})`;
              ctx.lineWidth = w;
              ctx.stroke(path);
            }
            // Bright white core — tracks the string, scales slightly
            // with velocity so loud plucks look hotter at the centre.
            ctx.strokeStyle = `rgba(255, 255, 255, ${intensity})`;
            ctx.lineWidth = physWidthPx + (1 + v * 3) * dpr;
            ctx.stroke(path);
          }
        }

        // --- Pills: note name left, Hz right ---
        const centsStr =
          Math.abs(tun.cents) >= 1
            ? ` ${tun.cents > 0 ? '+' : ''}${tun.cents.toFixed(0)}¢`
            : '';
        const noteLabel = `${tun.pitchClass}${tun.octave}${centsStr}`;
        const hz = tun.freq < 100 ? tun.freq.toFixed(1) : tun.freq.toFixed(0);
        const labelColor = anchorColor ?? (tun.fromOverride ? '#7dd3fc' : '#fff');
        const labelAlpha = isHome ? 1 : 0.85;

        drawPill(ctx, noteLabel, padX, yBase, 'left', pillPadH, pillPadV, labelColor, labelAlpha);
        drawPill(ctx, `${hz} Hz`, canvas.width - padX, yBase, 'right', pillPadH, pillPadV, labelColor, labelAlpha);
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [stringCount, activeIndex, mirrorCamera]);

  return <canvas ref={ref} className="strings-overlay" />;
}

/**
 * Sum of 2D Gaussian bumps, one per active bend. Each bend pulls the string
 * vertically from its baseline toward the fingertip (apexY), falling off
 * with σ = BEND_SIGMA_X laterally and BEND_SIGMA_Y along the string stack.
 */
function bendOffsetPx(
  xNorm: number,
  yBaseNorm: number,
  bends: BendVisual[],
  canvasH: number,
): number {
  let offset = 0;
  const twoSigmaXSq = 2 * BEND_SIGMA_X * BEND_SIGMA_X;
  const twoSigmaYSq = 2 * BEND_SIGMA_Y * BEND_SIGMA_Y;
  for (const b of bends) {
    const dx = xNorm - b.apexX;
    const dy = yBaseNorm - b.anchorY;
    const bump = Math.exp(-(dx * dx) / twoSigmaXSq - (dy * dy) / twoSigmaYSq);
    offset += (b.apexY - b.anchorY) * bump * canvasH;
  }
  return offset;
}

function buildStringPath(
  yBase: number,
  bends: BendVisual[],
  width: number,
  canvasH: number,
): Path2D {
  const p = new Path2D();
  if (bends.length === 0) {
    p.moveTo(0, yBase);
    p.lineTo(width, yBase);
    return p;
  }
  const yBaseNorm = yBase / canvasH;
  const steps = 32;
  for (let k = 0; k <= steps; k++) {
    const xNorm = k / steps;
    const x = xNorm * width;
    const y = yBase + bendOffsetPx(xNorm, yBaseNorm, bends, canvasH);
    if (k === 0) p.moveTo(x, y);
    else p.lineTo(x, y);
  }
  return p;
}

function drawPill(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: 'left' | 'right',
  padH: number,
  padV: number,
  color: string,
  alpha: number,
) {
  const metrics = ctx.measureText(text);
  const textW = metrics.width;
  const fontSize =
    (metrics.actualBoundingBoxAscent ?? 0) + (metrics.actualBoundingBoxDescent ?? 0) ||
    12 * devicePixelRatio;
  const h = fontSize + padV * 2;
  const w = textW + padH * 2;
  const rectX = align === 'left' ? x : x - w;
  const rectY = y - h / 2;

  ctx.save();
  ctx.fillStyle = 'rgba(10, 10, 15, 0.78)';
  roundRect(ctx, rectX, rectY, w, h, 6 * devicePixelRatio);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.fillText(text, rectX + padH, y);
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
